import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ListPropertyType } from './types';
import { useProperties } from '../../../views/CustomProperties/Properties/queries';
import { usePropertySets } from '../../../views/CustomProperties/PropertySets/queries';
import { Property } from '../../../views/CustomProperties/Properties/types';
import { PropertySet } from '../../../views/CustomProperties/PropertySets/types';

type HookMap = {
  [key in ListPropertyType]: (id: number) => ReturnType<typeof useQuery<Property[]|PropertySet[]>>;
};

const usePropertyFetchers: HookMap = {
  "property": (id:number) => useProperties([id], false),
  "propertySet": (id: number) => usePropertySets([id], false),
};

export const useDynamicPropertyLoader = (id: number | undefined, listPropertyType: ListPropertyType | '') => {
  if (!id) {
    throw new Error('id must be defined');
  }

  if (!listPropertyType) {
    throw new Error('listPropertyType must be defined');
  }

  // Dynamically fetch data using the appropriate hook
  const fetcherHook = usePropertyFetchers[listPropertyType];
  const queryResult = fetcherHook(id);

  const mappedData = useMemo(
    () => {
      if (queryResult.isLoading) {
        return undefined;
      }

      if (!queryResult.data || queryResult.isLoading) {
        throw new Error('Data is not available');
      }

      return queryResult.data[0];
    },
    [queryResult.data, listPropertyType]
  );

  return useMemo(
    () => ({
      ...queryResult,
      data: mappedData, // Only updates if queryResult or mappedData changes
    }),
    [queryResult, mappedData]
  );
};
